import {FlexColumn} from '@forecast-it/design-system';
import React, {useEffect, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import styled from 'styled-components';
import {dispatch, EVENT_ID} from '../../../../../../containers/event_manager';
import {hasFeatureFlag} from '../../../../util/FeatureUtil';
import {ChangeAgentAffordance} from '../../../nova-invocation/ChangeAgentAffordance';
import {showDiscardConversationConfirmationModal} from '../../../nova-invocation/modals/DiscardConversationConfirmationModal';
import {
	discardConversation,
	getRunState,
	invoke,
	markRead,
	RUN_CAP,
	subscribeToAvailableAgents,
	subscribeToRunState,
} from '../../../nova-invocation/novaInvocationStore';
import type {InvokeRefusal, NovaThreadTurn} from '../../../nova-invocation/novaInvocationStore';
import {useNovaAvailableAgents} from '../../../nova-invocation/useNovaAvailableAgents';
import {NovaConversationTurn, type NovaConversationTurnProps} from './NovaConversationTurn';
import {createToast} from '../../../toasts/another-toast/toaster';
import {replyTextFor, retryArgsFor} from './novaThreadTurn';
import {TASK_MODAL_CONTENT_INSET} from '../../taskModalLayout';

// Reuses NovaConversationTurn's own run shape rather than redeclaring it: the two components render
// the exact same server record (one run) or a list of it, so a redeclared, structurally-identical
// interface here would just be a second name for the first one to drift out of sync with.
export type NovaThreadRun = NovaConversationTurnProps['run'];

// Inset like every other section of the modal, so the conversation stays in the same column as the
// rest of the task.
const ThreadSection = styled(FlexColumn)`
	padding-left: ${TASK_MODAL_CONTENT_INSET};
	padding-right: ${TASK_MODAL_CONTENT_INSET};
	padding-bottom: 16px;
`;

export interface NovaConversationThreadProps {
	taskId: string;
	novaThread: ReadonlyArray<NovaThreadRun> | null;
	isTaskReadOnly: boolean;
	// Whether this person may act on the agent — start a run, steer one, or end one. Reading is
	// never gated, so the thread renders in full either way; what changes is that the controls
	// which would start or end something are refused, and say why.
	//
	// This is the one question the thread asks, and it already folds in the client-actions
	// restriction alongside the edit-tasks permission — the caller resolves both, because either one
	// on its own means this person cannot do the same work by hand.
	//
	// Separate from isTaskReadOnly, which withholds those controls outright. A locked task is a
	// state of the task that anybody looking at it can see from the rest of the modal; a permission
	// is not, so it is stated on the control rather than left to be inferred from its absence.
	// Absent means allowed, so a caller that does not pass it keeps its behaviour.
	canAct?: boolean;
	// Who is replying right now, for the turn that is still in flight. A saved turn names its own
	// author from the run that produced it; the pending one has no run yet, so the only honest
	// answer is whoever is sending it. Absent leaves the pending turn on the generic mark.
	replyAuthor?: {fullName: string | null; profilePictureId: string | null} | null;
	// True exactly once, for the mount that is the task modal landing on this task's conversation
	// (see novaLanding.ts) — never re-derived from a later prop change, so a socket update to the
	// task partway through a session does not re-trigger this. Absent/false leaves this component
	// exactly as it already behaves: it never scrolls anything on its own otherwise.
	revealMostRecentAnswerOnMount?: boolean;
	// Handed the most recent turn's own DOM node once it is safe to reveal — after the thread is
	// confirmed expanded, so there is something on screen to bring into view. This component has no
	// scroll container of its own (the task modal owns that, and its own scroll needs the same
	// open-agent-picker guard scrollToBottom already applies), so revealing is delegated back to
	// whoever mounted this rather than attempted here.
	onRevealMostRecentAnswer?: (node: HTMLElement) => void;
	// Told once, when a discard this component performed has been confirmed by the server. This
	// component removes itself either way (see isDiscarded below); the callback exists for the
	// siblings fed from the same fetched-once task query - the comment footer's reply mode reads the
	// same thread list, and a reply sent to a conversation that is no longer shown would produce an
	// answer nothing renders.
	onConversationDiscarded?: () => void;
}

// This instance key exists only to disambiguate this mount from the picker's three other mounting
// surfaces (ConnectedNovaInvocationControl's 'my-work-row'/'table-row'/'card') so novaInstanceKey's
// toggle/anchor lookup doesn't collide with whichever of those also happens to be on the page behind
// this modal — see NovaAgentPicker.tsx's own instanceKey doc.
const THREAD_INSTANCE_KEY = 'task-modal-thread';

// The whole task's Nova conversation, oldest run first. "Discarded", "never actioned" and "the run
// service was unreachable" all arrive here as the same empty array (see novaThreadTurn.ts's own
// note on hasAgentToRun for the same three-ways-to-one-answer shape) - deliberately one rendered
// state, not three: the modal simply shows no thread section rather than guessing which of the
// three happened.
export const NovaConversationThread = (props: NovaConversationThreadProps): JSX.Element | null => {
	const isEnabled = hasFeatureFlag('nova_agent_invocation');
	const {formatMessage} = useIntl();

	// Called unconditionally, before any early return below, per the Rules of Hooks - `enabled`
	// (not an early return) is what lets the hook skip its own store subscriptions when this thread
	// won't render anyway. This is deliberately not gated on the thread having any runs: a
	// deep-linked task modal can mount this component before any row or card control has run on the
	// page at all, and it is the only thing here that knows to ask for the agent list at all.
	useNovaAvailableAgents(isEnabled);

	// The agent list arrives asynchronously, and a turn resolves its run's agent id to a readable
	// name by reading that list at render time (see resolveAgentTitle). Nothing about writing the
	// list re-renders this tree on its own, so without this a thread that rendered before the fetch
	// resolved keeps showing the raw agent id for the life of the mount — which is every deep-linked
	// task modal, and any modal opened before some other mounted control happened to warm the list.
	// Whether the name appears must not depend on which component mounted first.
	const [, setAgentListVersion] = useState(0);
	useEffect(() => {
		if (!isEnabled) return undefined;
		return subscribeToAvailableAgents(() => setAgentListVersion(version => version + 1));
	}, [isEnabled]);

	// Whether this thread is shown as a single collapsed row or in full. Local to this mount rather
	// than stored anywhere shared - collapsing is a reading convenience for whoever has the modal
	// open right now, not a fact about the task that would mean anything to another viewer or another
	// session.
	const [isCollapsed, setIsCollapsed] = useState(false);
	// Read from the same store-backed run state the row and card controls use (novaInvocationStore's
	// getRunState/subscribeToRunState) rather than inferred from anything local - whether a run is in
	// flight for this task is a fact about the task, shared by every surface watching it, not
	// something this component could work out any other way.
	const [isRunning, setIsRunning] = useState(() => getRunState(props.taskId).isRunning);
	// What the person typed into the reply box, while their run is still in flight. Held so the
	// reply can be on screen the moment it is sent rather than when its answer lands — waiting for
	// the answer left the box emptying with nothing to show for it, which reads as the message
	// having gone nowhere.
	const [pendingMessage, setPendingMessage] = useState(() => getRunState(props.taskId).pendingMessage);

	// Turns produced by a reply that resolved during this mount, kept only until the server list
	// itself catches up with them (see the reset effect below). This is what lets a reply's answer
	// show up the moment its run resolves, without waiting for the modal to be closed and reopened -
	// the server list this component is otherwise handed (`props.novaThread`) is fetched once, on
	// mount, and never refetched on its own.
	const [appendedTurns, setAppendedTurns] = useState<NovaThreadTurn[]>([]);
	// Set only by a discard this mount performed and the server confirmed. The thread this component
	// renders comes from a query that ran when the modal opened and is never refetched, so the
	// discarded conversation would otherwise stay on screen until the modal was closed and reopened
	// - a person who has just been asked to confirm losing it would be looking straight at it.
	const [isDiscarded, setIsDiscarded] = useState(false);

	// The turn already sitting in this task's run state when the thread mounted, if any. Appending is
	// for replies that resolve while the modal is open; a turn that resolved before it opened is
	// already in the server list this component was handed, so re-adding it here would be a second
	// copy of a turn nobody produced twice.
	//
	// Held by object identity rather than by id: each run writes a fresh turn object, so identity
	// tells "this is the same turn being re-announced" apart from "this is a different run that
	// happens to carry the same id". The distinction matters because the store re-announces whatever
	// currently sits in `outcome` on every state change for the task, including changes that have
	// nothing to do with a new turn - marking the answer read among them.
	const turnAtMountRef = useRef(getRunState(props.taskId).outcome?.turn ?? null);

	useEffect(() => {
		return subscribeToRunState(props.taskId, () => {
			const state = getRunState(props.taskId);
			setIsRunning(state.isRunning);
			setPendingMessage(state.pendingMessage);
			// A thread left collapsed must not stay that way once a new run starts on it - the person
			// is about to see Nova's answer arrive, and a collapsed row would hide exactly that.
			if (state.isRunning) {
				setIsCollapsed(false);
				return;
			}
			// `outcome.turn` is a reply that landed somewhere the conversation can show: either a run
			// the run service carried out and read back after the write, or - for a refusal that wrote
			// the person's words down before refusing them - a turn standing in for the record it
			// wrote, until the next refetch replaces it with the row itself (see novaInvocationStore's
			// commitRun and turnForRecordedRefusal). A refusal invoke() raises itself (already running,
			// at the run cap, no agent, superseded) never reaches this callback at all - it resolves
			// invoke()'s own promise without writing to the store - and a refusal that recorded nothing
			// carries no turn, because the composer is still holding the only copy of that message.
			const turn = state.outcome?.turn;
			if (!turn) return;
			// Resolved before this mount - see turnAtMountRef.
			if (turn === turnAtMountRef.current) return;
			setAppendedTurns(current => (current.some(existing => existing.id === turn.id) ? current : [...current, turn]));
		});
	}, [props.taskId]);

	// The server-provided thread caught up (the modal was reopened and refetched it, or - were a
	// live update ever wired up - a socket update replaced it): whatever this mount appended on top
	// of the previous prop is either already folded into the new one or belongs to a run this new
	// list has no record of, and either way holding onto it is no longer this component's job. Reset
	// only fires on an actual prop change, never merely because a re-render handed the same thread
	// back again.
	const previousNovaThreadRef = useRef(props.novaThread);
	useEffect(() => {
		if (previousNovaThreadRef.current === props.novaThread) return;
		previousNovaThreadRef.current = props.novaThread;
		setAppendedTurns([]);
	}, [props.novaThread]);

	// Seeing the conversation is what reading it means, so the task's control stops announcing an
	// unread answer once this thread is on screen. Without this the announcement only cleared when
	// the control's own Ready chip was the thing clicked, so opening the task any other way - its
	// name in a row, a link, the board card - left it claiming an unread answer the person was
	// already looking at.
	//
	// markRead guards itself: it does nothing unless a run finished in this session and none is in
	// flight, so a task whose conversation was loaded from the server (already presented as read) and
	// one with a run still working are both left alone. A later run writes outputRead false again
	// when it completes, so the next answer announces itself normally.
	//
	// Once per mount: a re-render is not a second reading, and the person may have started another
	// run since.
	const hasMarkedReadRef = useRef(false);
	useEffect(() => {
		if (hasMarkedReadRef.current) return;
		if (isDiscarded) return;
		if (!props.novaThread || props.novaThread.length === 0) return;
		hasMarkedReadRef.current = true;
		markRead(props.taskId);
	}, [props.taskId, props.novaThread, isDiscarded]);

	// The most recent turn's own DOM node, kept only long enough to hand it to
	// onRevealMostRecentAnswer once - never read for anything else.
	const latestTurnRef = useRef<HTMLDivElement | null>(null);
	// Guards the reveal to a single call across this mount's whole lifetime, however many times the
	// effect below re-runs (every render, deliberately, so it can retry after forcing the thread
	// open) - a person who has since collapsed or replied to the thread themselves must not be
	// scrolled back to it by a re-render that has nothing to do with landing anymore.
	const hasRevealedRef = useRef(false);

	useEffect(() => {
		if (!props.revealMostRecentAnswerOnMount || hasRevealedRef.current) return;
		if (isCollapsed) {
			// Nothing to reveal inside a collapsed thread (behaviour: expand first) - expanding here
			// re-renders with every turn back on screen, and this effect runs again to finish the job.
			setIsCollapsed(false);
			return;
		}
		if (!latestTurnRef.current) return;
		hasRevealedRef.current = true;
		props.onRevealMostRecentAnswer?.(latestTurnRef.current);
	});

	if (!isEnabled) return null;
	// Deliberately no early return for a person who may not act. Reading a conversation is never
	// gated: the thread renders in full and the controls that would start or end a run are refused
	// on themselves, stating why. Returning null here hid an answer that had already been produced
	// on this task from somebody allowed to see the task, and left nothing on screen to explain it.
	if (isDiscarded) return null;

	// Appended turns go after the server-provided list (oldest-first is this thread's whole
	// ordering, and a turn this component appended for itself is by construction newer than
	// anything the server had when it handed over `props.novaThread`), and never twice: one already
	// present in `props.novaThread` - the server list caught up with it since it was appended - is
	// left out of the appended tail rather than rendered as a second copy of the same run.
	//
	// Merged before the empty check rather than after it: a task whose first run was started from its
	// row has an empty server list, because that list was fetched when the modal opened and the run
	// had produced nothing yet. Returning early on the empty list left the answer with nowhere to
	// land when it arrived, so the thread stayed blank and reply mode never appeared.
	const serverRuns: ReadonlyArray<NovaThreadRun> = props.novaThread ?? [];
	const serverRunIds = new Set(serverRuns.map(serverRun => serverRun.id));
	const allRuns: ReadonlyArray<NovaThreadRun> = [...serverRuns, ...appendedTurns.filter(turn => !serverRunIds.has(turn.id))];
	if (allRuns.length === 0) return null;

	// Only the newest thread is on screen, which is the same rule the server applies when it decides
	// what this task's conversation is. It matters when the agent is changed: that starts a fresh
	// thread and retires the one before it, and the retired runs are still in `props.novaThread`
	// because that list was fetched when the modal opened and is never refetched. Without this the
	// old agent's conversation stayed above the new one until the task was closed and reopened -
	// showing, as a live conversation, turns the backend had already discarded.
	//
	// Falls back to showing everything when the newest run names no thread, rather than emptying the
	// view: an unexpected null here is not a reason to hide a conversation that exists.
	const currentThreadId = allRuns[allRuns.length - 1]?.threadId ?? null;
	const novaThread: ReadonlyArray<NovaThreadRun> = currentThreadId
		? allRuns.filter(runOnThread => runOnThread.threadId === currentThreadId)
		: allRuns;
	// Losing the thread's replies is exactly what ChangeAgentAffordance's own confirmation guards
	// against - a single opening run has no reply to lose, so only a longer thread needs the warning.
	const hasReplies = novaThread.length > 1;
	// The affordance always offers to change *from* whichever agent most recently ran, not the
	// thread's opening agent - that is the choice a next run would otherwise repeat.
	const mostRecentRun = novaThread[novaThread.length - 1];
	// The opening run's saved text, which every later run repeats before its own reply.
	const openingUserMessageText = novaThread[0]?.userMessageText ?? null;
	const mostRecentAgentId = mostRecentRun.agentId ?? '';

	// Built here and placed by the newest turn, rather than rendered after the thread: the picker it
	// opens is anchored to its own button, so it has to stay one element wherever it is shown.
	// Absent means allowed — see the prop's own note. Read once here so every control below asks
	// the same question rather than each re-deriving it.
	const canAct = props.canAct !== false;
	const cannotActReason = formatMessage({id: 'nova_invocation.control.reason_no_edit_task'});

	const changeAgentAffordance = (
		<ChangeAgentAffordance
			taskId={props.taskId}
			instanceKey={THREAD_INSTANCE_KEY}
			currentAgentId={mostRecentAgentId}
			hasReplies={hasReplies}
			disabled={!canAct}
			disabledReason={cannotActReason}
			onAgentChanged={newAgentId => {
				invoke(props.taskId, newAgentId);
			}}
		/>
	);

	// Asked about before it happens, then performed against whichever turn is offering it: discard
	// is whole-conversation, so any turn of the thread identifies the thread to retire (see the run
	// service's own read side, which treats one discarded run as condemning the thread it belongs
	// to). The thread is only removed from the screen once the server confirms it went - a discard
	// that quietly matched nothing must not look like it worked.
	// What a refused retry says, for the two reasons that are genuinely a refusal.
	//
	// 'at_run_cap' is the one reachable from a control this component honestly offered: the cap
	// counts runs across every task, and nothing re-renders this thread when a run starts on a
	// different one, so the press is taken and then declined. 'already_running' and 'no_agent' are
	// held off by the guards on the control itself but share the generic sentence, because a press
	// that changes nothing and says nothing is indistinguishable from a broken control.
	//
	// 'stopped' and 'superseded' are silent. Neither is a refusal: the run started, and then lost
	// its place - 'stopped' because the person pressed Stop themselves, which the cancelled turn on
	// the thread already says. Telling them Nova "couldn't start that run" would be false, and would
	// blame the product for what they just did.
	const notifyRetryRefused = (reason: InvokeRefusal['reason']): void => {
		if (reason === 'stopped' || reason === 'superseded') return;
		createToast({
			duration: 5000,
			message:
				reason === 'at_run_cap'
					? formatMessage({id: 'nova_invocation.failure.retry_refused_at_run_cap'}, {count: RUN_CAP})
					: formatMessage({id: 'nova_invocation.failure.retry_refused'}),
		});
	};

	const confirmAndDiscard = (runId: string): void => {
		showDiscardConversationConfirmationModal({
			onConfirm: () => {
				void discardConversation(runId, props.taskId).then(discarded => {
					if (!discarded) {
						// Said out loud rather than left as a dialog that closes and changes nothing. The
						// conversation staying put is correct - the server declined - but silence reads
						// as the discard having worked and the screen not having caught up.
						createToast({duration: 5000, message: formatMessage({id: 'nova_thread.discard.declined'})});
						return;
					}
					setIsDiscarded(true);
					props.onConversationDiscarded?.();
				});
			},
		});
	};

	return (
		<ThreadSection data-cy="nova-thread">
			{isCollapsed ? (
				// The summary row is a turn wearing its own header and nothing else, rather than a
				// second row built to look like one. One header, one chevron, one place the brand
				// name and the agent chip are laid out — a parallel row would be two things obliged
				// to agree about how a folded thread introduces itself.
				//
				// The newest run, not the opening one whose header carried the control that folded
				// the thread: what the row reports is where the conversation has got to, and naming
				// the first agent and the first time would describe the thread as it was rather than
				// as it stands.
				<NovaConversationTurn
					run={mostRecentRun}
					collapsed
					hiddenNote={formatMessage({id: 'nova_thread.collapse.hidden_count'}, {count: novaThread.length})}
					onExpand={() => setIsCollapsed(false)}
				/>
			) : (
				// Where each of the conversation's controls goes is a property of the turn's place in
				// the thread, not of the turn itself:
				//  - the sources belong to the opening turn, because they describe the conversation as a
				//    whole and would otherwise repeat under every answer;
				//  - collapse belongs there too - it is the header of the thread, so the control that
				//    folds the thread away sits in it;
				//  - change agent and discard belong to the newest turn's action row - change agent beside
				//    copy, discard at the far end: both act on the conversation from where it has got to.
				novaThread.map((run, index) => {
					const isOpeningTurn = index === 0;
					// What the person typed, dug out of the text this run saved - see replyTextFor for
					// why a reply's saved text carries the whole prompt ahead of it. Null on the
					// opening run, whose saved text is that prompt and nothing else.
					const replyText = isOpeningTurn ? null : replyTextFor(run.userMessageText, openingUserMessageText);
					const isMostRecent = index === novaThread.length - 1;
					// Running a failed turn again. What it would take is novaThreadTurn's decision;
					// whether to offer it at all is this component's, and the answers are the ones
					// discard already gives:
					//  - only on a turn that failed, which is the only turn with nothing to show for
					//    itself;
					//  - never on a read-only task, where no control starts work;
					//  - never while a run is in flight - invoke() refuses a second one for the same
					//    task outright, so a live-looking button would do nothing when pressed;
					//  - only on the newest turn, because every older one already has a run after it
					//    and re-sending it would fork the conversation rather than resume it.
					// Permission is the exception to that list: it is stated on the control instead of
					// taking it away, so the reply offers a reason rather than an absence.
					const retryArgs =
						run.outcome === 'FAILED'
							? retryArgsFor({
									isOpeningTurn,
									agentId: run.agentId,
									threadId: run.threadId,
									replyText,
									replyAuthor: props.replyAuthor,
							  })
							: null;
					const onRetry =
						retryArgs && !props.isTaskReadOnly && !isRunning && isMostRecent
							? () =>
									void invoke(props.taskId, retryArgs.agentId, retryArgs.opts).then(result => {
										if (result.kind === 'refused') notifyRetryRefused(result.reason);
									})
							: undefined;
					const turn = (
						<NovaConversationTurn
							run={run}
							replyText={replyText}
							showGrounding={isOpeningTurn}
							changeAgentAffordance={isMostRecent && !props.isTaskReadOnly ? changeAgentAffordance : undefined}
							// Offered on the newest turn only, and never on a read-only task or while a
							// run is in flight. A reply continues the conversation from where it has got
							// to, so a control on an older turn would promise to answer that turn and do
							// something else; and a thread already waiting on an answer has nothing to
							// reply to yet.
							onReplyToNova={
								props.isTaskReadOnly || isRunning || !isMostRecent
									? undefined
									: () => dispatch(EVENT_ID.NOVA_REQUEST_REPLY, props.taskId)
							}
							// Shown refused rather than taken away, the same as every other control in
							// this row: a locked task is a state of the task anyone can see from the rest
							// of the modal, but a missing permission is not, so an absent control would
							// leave this person nothing to read.
							replyDisabled={!canAct}
							replyDisabledReason={cannotActReason}
							// Whether this person could start another run on this task at all. Read by the
							// failure block, which otherwise closes by pointing at the task's own control -
							// the wrong thing to tell someone that control is withheld from.
							canInvokeAgain={!props.isTaskReadOnly && canAct}
							discardDisabled={!canAct}
							discardDisabledReason={cannotActReason}
							onRetry={onRetry}
							retryDisabled={!canAct}
							retryDisabledReason={cannotActReason}
							// Absent, not disabled, while a run is in flight: behaviour 7 already forces this
							// thread back open the moment a new run starts, so there is never a moment where a
							// still-running thread is sitting collapsed with a dead collapse button to explain.
							onCollapse={isRunning || !isOpeningTurn ? undefined : () => setIsCollapsed(true)}
							// Offered on the newest turn only, and never on a read-only task or while a run
							// is in flight. One control, not one per turn: every turn would discard the same
							// conversation, so a row of identical Discard buttons down a long thread would
							// suggest each one retired only the turn it sits on.
							onDiscard={
								props.isTaskReadOnly || isRunning || !isMostRecent ? undefined : () => confirmAndDiscard(run.id)
							}
							// A thread of one answer discards that answer; a conversation with replies in it
							// discards all of them, and the control says which it is about to do.
							discardLabel={formatMessage({
								id: hasReplies ? 'nova_thread.actions.discard_all' : 'nova_thread.actions.discard',
							})}
						/>
					);
					// Every turn gets the same wrapper, and only the last one is handed the ref that makes
					// it revealable (see latestTurnRef above). Uniform on purpose: giving the last turn a
					// div and the others a Fragment would change an element's *type* the moment a reply
					// arrives, and React unmounts a subtree whose type changed - taking with it the
					// newest turn's change-agent affordance and any picker open inside it. A plain div
					// costs nothing here, since FlexColumn's spacing applies to its direct children
					// whatever they contain.
					return (
						<div key={run.id} ref={isMostRecent ? latestTurnRef : undefined}>
							{turn}
						</div>
					);
				})
			)}
			{/* The reply just sent, with Nova working under it. Rendered from the store's own
			    pendingMessage rather than from anything this component held onto, so it is the same
			    fact every other surface reads — and so a reply sent from somewhere else in the modal
			    still shows up here.

			    A run in flight with no message is an opening run, which has no reply to show and
			    whose own spinner is the task's control; only a reply renders this. Keyed apart from
			    the saved turns so React never reconciles it against one — it becomes a real turn
			    with a real id the moment the run resolves, and the two must not be mistaken for each
			    other mid-swap. */}
			{isRunning && pendingMessage && (
				<div ref={latestTurnRef} data-cy="nova-thread-pending-turn">
					<NovaConversationTurn
						run={{
							id: 'pending',
							threadId: currentThreadId,
							agentId: mostRecentAgentId,
							status: null,
							outcome: null,
							answer: null,
							userMessageText: null,
							actingPerson: props.replyAuthor ?? null,
							ranAt: null,
							failureState: null,
							grounding: null,
						}}
						replyText={pendingMessage}
						pending
					/>
				</div>
			)}
		</ThreadSection>
	);
};
